import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {
    SketchClosureTraversalDirection,
    type SketchClosureSourceEndpoints,
    type SketchClosureTraversalStep,
} from './SketchClosureTypes';

//------ 草图闭合模块化 开始------
/**
 * 集合源端点访问协议。
 *
 * 说明：
 * 1. 闭合判定只依赖“可读取起点与终点”的边协议；
 * 2. 不要求各边对象继承额外基类，避免污染既有草图对象层级；
 * 3. 协议缺失时直接抛出明确错误，便于定位非法边对象。
 */
interface SketchClosureSourceEndpointAccessor {
    /** 获取起点。 */
    getEdgeStartPoint: () => SketchPointObject;
    /** 获取终点。 */
    getEdgeEndPoint: () => SketchPointObject;
}

/**
 * 草图闭合端点适配器。
 *
 * 设计说明：
 * 1. 统一封装边对象端点读取与遍历方向换算；
 * 2. 绘制、Trim、轮廓采样都通过该类获得一致的端点语义；
 * 3. 保持无状态实现，便于测试和后续扩展更多边类型。
 */
export class SketchClosureEndpointAdapter {
    /**
     * 获取集合源对象的起止端点。
     *
     * @param source 集合源对象。
     * @returns 起点与终点。
     * @throws 当集合源对象未同时提供 `getEdgeStartPoint` 和 `getEdgeEndPoint` 访问方法时抛出错误。
     */
    public getSourceEndpoints(source: CollectSource): SketchClosureSourceEndpoints {
        const startGetter = Reflect.get(source, 'getEdgeStartPoint');
        const endGetter = Reflect.get(source, 'getEdgeEndPoint');
        if (typeof startGetter !== 'function' || typeof endGetter !== 'function') {
            throw new Error('集合源对象缺少端点访问协议，无法读取起止点。');
        }

        const endpointAccessor = source as unknown as SketchClosureSourceEndpointAccessor;
        return {
            startPoint: endpointAccessor.getEdgeStartPoint(),
            endPoint: endpointAccessor.getEdgeEndPoint(),
        };
    }

    /**
     * 为单个集合源创建正反两个遍历步骤。
     *
     * @param source 集合源对象。
     * @returns 当前源对象的候选遍历步骤。
     */
    public createTraversalStepsForSource(source: CollectSource): SketchClosureTraversalStep[] {
        return [
            this.createTraversalStep(source, SketchClosureTraversalDirection.Forward),
            this.createTraversalStep(source, SketchClosureTraversalDirection.Reversed),
        ];
    }

    /**
     * 获取遍历步骤在当前方向下的起止端点。
     *
     * @param step 遍历步骤。
     * @returns 当前方向对应的起止端点。
     */
    public getTraversalStepEndpoints(step: SketchClosureTraversalStep): SketchClosureSourceEndpoints {
        const endpoints = this.getSourceEndpoints(step.source);
        if (!step.reversed) {
            return endpoints;
        }
        return {
            startPoint: endpoints.endPoint,
            endPoint: endpoints.startPoint,
        };
    }

    /**
     * 创建指定方向的遍历步骤。
     *
     * @param source 集合源对象。
     * @param direction 遍历方向。
     * @returns 遍历步骤。
     */
    private createTraversalStep(
        source: CollectSource,
        direction: SketchClosureTraversalDirection,
    ): SketchClosureTraversalStep {
        return {
            source,
            reversed: direction === SketchClosureTraversalDirection.Reversed,
        };
    }
}
//------ 草图闭合模块化 结束------
